/**
 * Static lookup of NWS office locations with lat/lon coordinates.
 * Used by the Weather Stories Viewer to render office dots on the SVG map.
 *
 * @typedef {Object} NWSOfficeLocation
 * @property {string} id - NWS office identifier (e.g., "LOX")
 * @property {string} name - Human-readable office name (e.g., "Los Angeles/Oxnard, CA")
 * @property {number} lat - Latitude (-90 to 90)
 * @property {number} lon - Longitude (-180 to 180)
 */

/** @type {NWSOfficeLocation[]} */
const NWS_OFFICE_LOCATIONS = [
  { id: 'ABQ', name: 'Albuquerque, NM', lat: 35.04, lon: -106.62 },
  { id: 'ABR', name: 'Aberdeen, SD', lat: 45.45, lon: -98.43 },
  { id: 'AFC', name: 'Anchorage, AK', lat: 61.17, lon: -150.02 },
  { id: 'AFG', name: 'Fairbanks, AK', lat: 64.84, lon: -147.72 },
  { id: 'AJK', name: 'Juneau, AK', lat: 58.36, lon: -134.58 },
  { id: 'AKQ', name: 'Wakefield, VA', lat: 36.98, lon: -76.92 },
  { id: 'ALY', name: 'Albany, NY', lat: 42.69, lon: -73.83 },
  { id: 'AMA', name: 'Amarillo, TX', lat: 35.23, lon: -101.71 },
  { id: 'APX', name: 'Gaylord, MI', lat: 44.91, lon: -84.72 },
  { id: 'ARX', name: 'La Crosse, WI', lat: 43.82, lon: -91.19 },
  { id: 'BGM', name: 'Binghamton, NY', lat: 42.21, lon: -75.98 },
  { id: 'BIS', name: 'Bismarck, ND', lat: 46.77, lon: -100.76 },
  { id: 'BMX', name: 'Birmingham, AL', lat: 33.18, lon: -86.77 },
  { id: 'BOI', name: 'Boise, ID', lat: 43.57, lon: -116.22 },
  { id: 'BOU', name: 'Denver/Boulder, CO', lat: 39.99, lon: -105.26 },
  { id: 'BOX', name: 'Boston/Norton, MA', lat: 41.96, lon: -71.13 },
  { id: 'BRO', name: 'Brownsville, TX', lat: 25.92, lon: -97.42 },
  { id: 'BTV', name: 'Burlington, VT', lat: 44.47, lon: -73.15 },
  { id: 'BUF', name: 'Buffalo, NY', lat: 42.94, lon: -78.74 },
  { id: 'BYZ', name: 'Billings, MT', lat: 45.75, lon: -108.57 },
  { id: 'CAE', name: 'Columbia, SC', lat: 33.95, lon: -81.12 },
  { id: 'CAR', name: 'Caribou, ME', lat: 46.87, lon: -68.02 },
  { id: 'CHS', name: 'Charleston, SC', lat: 32.90, lon: -80.03 },
  { id: 'CLE', name: 'Cleveland, OH', lat: 41.41, lon: -81.86 },
  { id: 'CRP', name: 'Corpus Christi, TX', lat: 27.78, lon: -97.51 },
  { id: 'CTP', name: 'State College, PA', lat: 40.79, lon: -77.87 },
  { id: 'CYS', name: 'Cheyenne, WY', lat: 41.15, lon: -104.81 },
  { id: 'DDC', name: 'Dodge City, KS', lat: 37.77, lon: -99.97 },
  { id: 'DLH', name: 'Duluth, MN', lat: 46.83, lon: -92.21 },
  { id: 'DMX', name: 'Des Moines, IA', lat: 41.73, lon: -93.72 },
  { id: 'DTX', name: 'Detroit/Pontiac, MI', lat: 42.70, lon: -83.47 },
  { id: 'EAX', name: 'Kansas City/Pleasant Hill, MO', lat: 38.81, lon: -94.26 },
  { id: 'EKA', name: 'Eureka, CA', lat: 40.80, lon: -124.16 },
  { id: 'EPZ', name: 'El Paso, TX/Santa Teresa, NM', lat: 31.87, lon: -106.70 },
  { id: 'EWX', name: 'Austin/San Antonio, TX', lat: 29.70, lon: -98.03 },
  { id: 'FFC', name: 'Peachtree City, GA', lat: 33.36, lon: -84.57 },
  { id: 'FGF', name: 'Grand Forks, ND', lat: 47.92, lon: -97.09 },
  { id: 'FGZ', name: 'Flagstaff, AZ', lat: 35.23, lon: -111.82 },
  { id: 'FSD', name: 'Sioux Falls, SD', lat: 43.59, lon: -96.73 },
  { id: 'FWD', name: 'Dallas/Fort Worth, TX', lat: 32.83, lon: -97.30 },
  { id: 'GGW', name: 'Glasgow, MT', lat: 48.21, lon: -106.63 },
  { id: 'GID', name: 'Hastings, NE', lat: 40.59, lon: -98.38 },
  { id: 'GJT', name: 'Grand Junction, CO', lat: 39.12, lon: -108.53 },
  { id: 'GLD', name: 'Goodland, KS', lat: 39.37, lon: -101.70 },
  { id: 'GRB', name: 'Green Bay, WI', lat: 44.50, lon: -88.13 },
  { id: 'GRR', name: 'Grand Rapids, MI', lat: 42.89, lon: -85.55 },
  { id: 'GSP', name: 'Greenville-Spartanburg, SC', lat: 34.88, lon: -82.22 },
  { id: 'GYX', name: 'Gray/Portland, ME', lat: 43.89, lon: -70.26 },
  { id: 'HFO', name: 'Honolulu, HI', lat: 21.32, lon: -157.93 },
  { id: 'HGX', name: 'Houston/Galveston, TX', lat: 29.47, lon: -95.08 },
  { id: 'HNX', name: 'San Joaquin Valley/Hanford, CA', lat: 36.31, lon: -119.63 },
  { id: 'HUN', name: 'Huntsville, AL', lat: 34.72, lon: -86.66 },
  { id: 'ICT', name: 'Wichita, KS', lat: 37.65, lon: -97.44 },
  { id: 'ILM', name: 'Wilmington, NC', lat: 34.27, lon: -77.91 },
  { id: 'ILN', name: 'Wilmington, OH', lat: 39.42, lon: -83.82 },
  { id: 'ILX', name: 'Lincoln, IL', lat: 40.15, lon: -89.34 },
  { id: 'IND', name: 'Indianapolis, IN', lat: 39.71, lon: -86.28 },
  { id: 'IWX', name: 'Northern Indiana, IN', lat: 41.36, lon: -85.70 },
  { id: 'JAN', name: 'Jackson, MS', lat: 32.32, lon: -90.08 },
  { id: 'JAX', name: 'Jacksonville, FL', lat: 30.48, lon: -81.70 },
  { id: 'JKL', name: 'Jackson, KY', lat: 37.59, lon: -83.31 },
  { id: 'KEY', name: 'Key West, FL', lat: 24.55, lon: -81.75 },
  { id: 'LBF', name: 'North Platte, NE', lat: 41.13, lon: -100.68 },
  { id: 'LCH', name: 'Lake Charles, LA', lat: 30.13, lon: -93.22 },
  { id: 'LIX', name: 'New Orleans/Baton Rouge, LA', lat: 30.34, lon: -89.83 },
  { id: 'LKN', name: 'Elko, NV', lat: 40.87, lon: -115.74 },
  { id: 'LMK', name: 'Louisville, KY', lat: 38.23, lon: -85.66 },
  { id: 'LOT', name: 'Chicago, IL', lat: 41.60, lon: -88.08 },
  { id: 'LOX', name: 'Los Angeles/Oxnard, CA', lat: 34.20, lon: -119.20 },
  { id: 'LSX', name: 'St. Louis, MO', lat: 38.70, lon: -90.68 },
  { id: 'LUB', name: 'Lubbock, TX', lat: 33.53, lon: -101.85 },
  { id: 'LWX', name: 'Baltimore/Washington, DC', lat: 38.97, lon: -77.49 },
  { id: 'LZK', name: 'Little Rock, AR', lat: 34.83, lon: -92.26 },
  { id: 'MAF', name: 'Midland/Odessa, TX', lat: 31.94, lon: -102.19 },
  { id: 'MEG', name: 'Memphis, TN', lat: 35.06, lon: -89.99 },
  { id: 'MFL', name: 'Miami, FL', lat: 25.75, lon: -80.38 },
  { id: 'MFR', name: 'Medford, OR', lat: 42.37, lon: -122.87 },
  { id: 'MHX', name: 'Morehead City, NC', lat: 34.78, lon: -76.88 },
  { id: 'MKX', name: 'Milwaukee/Sullivan, WI', lat: 42.97, lon: -88.55 },
  { id: 'MLB', name: 'Melbourne, FL', lat: 28.11, lon: -80.65 },
  { id: 'MOB', name: 'Mobile, AL', lat: 30.68, lon: -88.24 },
  { id: 'MPX', name: 'Twin Cities/Chanhassen, MN', lat: 44.85, lon: -93.57 },
  { id: 'MQT', name: 'Marquette, MI', lat: 46.53, lon: -87.55 },
  { id: 'MRX', name: 'Morristown, TN', lat: 36.17, lon: -83.40 },
  { id: 'MSO', name: 'Missoula, MT', lat: 46.92, lon: -114.09 },
  { id: 'MTR', name: 'San Francisco Bay Area, CA', lat: 36.60, lon: -121.90 },
  { id: 'OAX', name: 'Omaha/Valley, NE', lat: 41.32, lon: -96.37 },
  { id: 'OHX', name: 'Nashville, TN', lat: 36.25, lon: -86.56 },
  { id: 'OKX', name: 'New York, NY', lat: 40.87, lon: -72.86 },
  { id: 'OTX', name: 'Spokane, WA', lat: 47.68, lon: -117.63 },
  { id: 'OUN', name: 'Norman, OK', lat: 35.24, lon: -97.46 },
  { id: 'PAH', name: 'Paducah, KY', lat: 37.07, lon: -88.77 },
  { id: 'PBZ', name: 'Pittsburgh, PA', lat: 40.53, lon: -80.22 },
  { id: 'PDT', name: 'Pendleton, OR', lat: 45.69, lon: -118.85 },
  { id: 'PHI', name: 'Mount Holly/Philadelphia, NJ', lat: 39.97, lon: -74.81 },
  { id: 'PQR', name: 'Portland, OR', lat: 45.56, lon: -122.53 },
  { id: 'PSR', name: 'Phoenix, AZ', lat: 33.44, lon: -112.02 },
  { id: 'PUB', name: 'Pueblo, CO', lat: 38.29, lon: -104.52 },
  { id: 'RAH', name: 'Raleigh, NC', lat: 35.87, lon: -78.79 },
  { id: 'REV', name: 'Reno, NV', lat: 39.57, lon: -119.80 },
  { id: 'RIW', name: 'Riverton, WY', lat: 43.07, lon: -108.48 },
  { id: 'RLX', name: 'Charleston, WV', lat: 38.31, lon: -81.72 },
  { id: 'RNK', name: 'Blacksburg, VA', lat: 37.21, lon: -80.41 },
  { id: 'SEW', name: 'Seattle/Tacoma, WA', lat: 47.69, lon: -122.26 },
  { id: 'SGF', name: 'Springfield, MO', lat: 37.24, lon: -93.40 },
  { id: 'SGX', name: 'San Diego, CA', lat: 32.90, lon: -117.05 },
  { id: 'SHV', name: 'Shreveport, LA', lat: 32.45, lon: -93.84 },
  { id: 'SJT', name: 'San Angelo, TX', lat: 31.37, lon: -100.49 },
  { id: 'SJU', name: 'San Juan, PR', lat: 18.43, lon: -66.01 },
  { id: 'SLC', name: 'Salt Lake City, UT', lat: 40.77, lon: -111.96 },
  { id: 'STO', name: 'Sacramento, CA', lat: 38.60, lon: -121.38 },
  { id: 'TAE', name: 'Tallahassee, FL', lat: 30.45, lon: -84.30 },
  { id: 'TBW', name: 'Tampa Bay, FL', lat: 27.70, lon: -82.40 },
  { id: 'TFX', name: 'Great Falls, MT', lat: 47.46, lon: -111.38 },
  { id: 'TOP', name: 'Topeka, KS', lat: 39.07, lon: -95.63 },
  { id: 'TSA', name: 'Tulsa, OK', lat: 36.15, lon: -95.87 },
  { id: 'TWC', name: 'Tucson, AZ', lat: 32.23, lon: -110.95 },
  { id: 'UNR', name: 'Rapid City, SD', lat: 44.07, lon: -103.21 },
  { id: 'VEF', name: 'Las Vegas, NV', lat: 36.04, lon: -115.18 },
];

export default NWS_OFFICE_LOCATIONS;
